import React, { useState, useEffect } from 'react';
import { hobbies, hobbiesByCategory } from '../data/hobbies';
import { X, ChevronDown } from 'lucide-react';

const HobbiesDropdown = ({ selectedHobbies = [], onHobbiesChange, label = 'Hobbies & Interests', required = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Only allow hobbies that are in the official list (e.g. filter out old profile/session data like "Shopping", "Meditation")
  useEffect(() => {
    const valid = selectedHobbies.filter((h) => hobbies.includes(h));
    if (valid.length !== selectedHobbies.length && typeof onHobbiesChange === 'function') {
      onHobbiesChange(valid);
    }
  }, [selectedHobbies, onHobbiesChange]);

  const filteredHobbies = searchTerm
    ? hobbies.filter(h => h.toLowerCase().includes(searchTerm.toLowerCase()))
    : hobbies;

  const handleToggleHobby = (hobby) => {
    if (selectedHobbies.includes(hobby)) {
      onHobbiesChange(selectedHobbies.filter(h => h !== hobby));
    } else {
      onHobbiesChange([...selectedHobbies, hobby]);
    }
  };

  const handleRemoveHobby = (hobby) => {
    onHobbiesChange(selectedHobbies.filter(h => h !== hobby));
  };

  return (
    <div className="relative">
      <label className="block text-xs font-semibold text-gray-900 dark:text-[#e5e5e5] mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      
      {/* Selected Hobbies Display */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2.5 rounded-xl text-[14px] font-medium border border-gray-300 dark:border-[#3d3d3d] bg-white dark:bg-[#252525] text-gray-900 dark:text-[#e5e5e5] focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all cursor-pointer min-h-[40px] flex items-center flex-wrap gap-1 shadow-sm hover:shadow-md"
      >
        {selectedHobbies.length > 0 ? (
          <>
            {selectedHobbies.map(hobby => (
              <span
                key={hobby}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveHobby(hobby);
                }}
                className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-gray-900 dark:bg-gray-700 text-white text-[12px] rounded-full hover:bg-gray-800 dark:hover:bg-gray-600 transition-colors"
              >
                {hobby}
                <X className="h-2.5 w-2.5" />
              </span>
            ))}
          </>
        ) : (
          <span className="text-gray-400 dark:text-gray-500 text-[14px]">Enter hobbies</span>
        )}
        <ChevronDown className={`h-3.5 w-3.5 text-gray-400 dark:text-gray-500 ml-auto transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
      </div>

      {/* Dropdown Menu - dark theme when in dark mode */}
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-[9999]"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute z-[10000] w-full mt-1.5 bg-white dark:bg-[#252525] rounded-xl border border-gray-200 dark:border-[#3d3d3d] shadow-xl max-h-56 overflow-hidden">
            {/* Search Input */}
            <div className="p-2 border-b border-gray-100 dark:border-[#3d3d3d]">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search hobbies"
                className="w-full px-3 py-2 rounded-lg text-[14px] font-medium border border-gray-200 dark:border-[#3d3d3d] bg-gray-50 dark:bg-[#181818] text-gray-900 dark:text-[#e5e5e5] placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50"
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            {/* Hobbies List */}
            <div className="max-h-44 overflow-y-auto">
              {Object.entries(hobbiesByCategory).map(([category, categoryHobbies]) => {
                const filteredCategoryHobbies = categoryHobbies.filter(h => 
                  filteredHobbies.includes(h)
                );
                
                if (filteredCategoryHobbies.length === 0) return null;

                return (
                  <div key={category} className="border-b border-gray-100 dark:border-[#3d3d3d] last:border-b-0">
                    <div className="px-3 py-1.5 bg-gray-50 dark:bg-[#1f1f1f] text-[12px] font-semibold text-gray-700 dark:text-gray-400 uppercase tracking-wide">
                      {category}
                    </div>
                    {filteredCategoryHobbies.map(hobby => (
                      <button
                        key={hobby}
                        type="button"
                        onClick={() => handleToggleHobby(hobby)}
                        className={`w-full text-left px-3 py-2 text-[14px] font-medium hover:bg-gray-50 dark:hover:bg-[#2d2d2d] transition-colors ${
                          selectedHobbies.includes(hobby) ? 'bg-gray-100 dark:bg-[#2d2d2d] font-semibold' : ''
                        }`}
                      >
                        <span className="text-gray-900 dark:text-[#e5e5e5]">
                          {selectedHobbies.includes(hobby) && '✓ '}
                          {hobby}
                        </span>
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default HobbiesDropdown;

